import { createBrowserRouter, Navigate } from "react-router-dom";
import App from "../App";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { RootRedirect } from "../components/RootRedirect";
import { RouteErrorBoundary } from "../components/ErrorBoundary";
import LoginPage from "../pages/auth/LoginPage";
import RegisterPage from "../pages/auth/RegisterPage";
import VerifyEmailPage from "../pages/auth/VerifyEmailPage";
import ForgotPasswordPage from "../pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";
import DashboardPage from "../pages/dashboard/DashboardPage";
import OrganizationListPage from "../pages/organizations/OrganizationListPage";
import OrganizationDetailPage from "../pages/organizations/OrganizationDetailPage";
import CreateOrganizationPage from "../pages/organizations/CreateOrganizationPage";
import OrganizationSettingsPage from "../pages/organizations/OrganizationSettingsPage";
import InviteMemberPage from "../pages/organizations/InviteMemberPage";
import TeamListPage from "../pages/teams/TeamListPage";
import TeamDetailPage from "../pages/teams/TeamDetailPage";
import CreateTeamPage from "../pages/teams/CreateTeamPage";
import AddTeamMemberPage from "../pages/teams/AddTeamMemberPage";
import BillingPage from "../pages/organizations/BillingPage";
import ApiKeysPage from "../pages/organizations/ApiKeysPage";
import PodcastListPage from "../pages/podcasts/PodcastListPage";
import CreatePodcastPage from "../pages/podcasts/CreatePodcastPage";
import PodcastSettingsPage from "../pages/podcasts/PodcastSettingsPage";
import EpisodeListPage from "../pages/podcasts/EpisodeListPage";
import EpisodeEditorPage from "../pages/podcasts/EpisodeEditorPage";
import ImportPodcastPage from "../pages/podcasts/ImportPodcastPage";
import PodcastAnalyticsPage from "../pages/podcasts/PodcastAnalyticsPage";
import EpisodeAnalyticsPage from "../pages/podcasts/EpisodeAnalyticsPage";
import SubscribersPage from "../pages/podcasts/SubscribersPage";
import NetworkListPage from "../pages/networks/NetworkListPage";
import CreateNetworkPage from "../pages/networks/CreateNetworkPage";
import NetworkSettingsPage from "../pages/networks/NetworkSettingsPage";
import NetworkMembershipsPage from "../pages/networks/NetworkMembershipsPage";
import NetworkAnalyticsPage from "../pages/networks/NetworkAnalyticsPage";
import SubscribePage, { subscribeLoader } from "../pages/public/SubscribePage";
import SubscribeSuccessPage from "../pages/public/SubscribeSuccessPage";

import {
  loginAction,
  registerAction,
  forgotPasswordAction,
  resetPasswordAction,
  resendVerificationAction,
  createOrganizationAction,
  updateOrganizationAction,
  deleteOrganizationAction,
  inviteMemberAction,
  updateMemberRoleAction,
  removeMemberAction,
  createTeamAction,
  updateTeamAction,
  deleteTeamAction,
  addTeamMemberAction,
  removeTeamMemberAction,
  createCheckoutAction,
  createPortalAction,
  cancelSubscriptionAction,
  createApiKeyAction,
  updateApiKeyAction,
  revokeApiKeyAction,
  rotateApiKeyAction,
  createPodcastAction,
  updatePodcastAction,
  deletePodcastAction,
  createNetworkAction,
  updateNetworkAction,
  deleteNetworkAction,
  createTierAction,
  updateTierAction,
  deleteTierAction,
  reorderTiersAction,
  invitePodcastAction,
  acceptMembershipAction,
  rejectMembershipAction,
  leaveMembershipAction,
  updateMembershipSplitAction,
  createEpisodeAction,
  updateEpisodeAction,
  deleteEpisodeAction,
} from "./actions";

import {
  organizationDetailLoader,
  organizationSettingsLoader,
  inviteMemberLoader,
  teamListLoader,
  teamDetailLoader,
  addTeamMemberLoader,
  createTeamLoader,
  billingLoader,
  apiKeysLoader,
  podcastListLoader,
  createPodcastLoader,
  podcastDetailLoader,
  episodeListLoader,
  createEpisodeLoader,
  episodeEditorLoader,
  podcastAnalyticsLoader,
  episodeAnalyticsLoader,
  networkListLoader,
  networkDetailLoader,
  networkMembershipsLoader,
  createNetworkLoader,
  networkAnalyticsLoader,
  subscribersLoader,
  dashboardLoader,
} from "./loaders";

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <RootRedirect />,
      },
      {
        path: "login",
        element: <LoginPage />,
        action: loginAction,
      },
      {
        path: "register",
        element: <RegisterPage />,
        action: registerAction,
      },
      {
        path: "verify-email",
        element: <VerifyEmailPage />,
      },
      {
        path: "api/auth/resend-verification-action",
        action: resendVerificationAction,
      },
      {
        path: "forgot-password",
        element: <ForgotPasswordPage />,
        action: forgotPasswordAction,
      },
      {
        path: "reset-password/:token",
        element: <ResetPasswordPage />,
        action: resetPasswordAction,
      },
      {
        path: "dashboard",
        element: (
          <ProtectedRoute requireEmailVerified>
            <DashboardPage />
          </ProtectedRoute>
        ),
        loader: dashboardLoader,
      },
      {
        path: "organizations",
        element: (
          <ProtectedRoute requireEmailVerified>
            <OrganizationListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "organizations/create",
        element: (
          <ProtectedRoute requireEmailVerified>
            <CreateOrganizationPage />
          </ProtectedRoute>
        ),
        action: createOrganizationAction,
      },
      {
        path: "organizations/:id",
        element: (
          <ProtectedRoute requireEmailVerified>
            <OrganizationDetailPage />
          </ProtectedRoute>
        ),
        loader: organizationDetailLoader,
      },
      {
        path: "organizations/:id/settings",
        element: (
          <ProtectedRoute requireEmailVerified>
            <OrganizationSettingsPage />
          </ProtectedRoute>
        ),
        loader: organizationSettingsLoader,
        action: updateOrganizationAction,
      },
      {
        path: "organizations/:id/settings/delete",
        action: deleteOrganizationAction,
      },
      {
        path: "organizations/:id/billing",
        element: (
          <ProtectedRoute requireEmailVerified>
            <BillingPage />
          </ProtectedRoute>
        ),
        loader: billingLoader,
      },
      {
        path: "organizations/:id/billing/checkout",
        action: createCheckoutAction,
      },
      {
        path: "organizations/:id/billing/portal",
        action: createPortalAction,
      },
      {
        path: "organizations/:id/billing/cancel",
        action: cancelSubscriptionAction,
      },
      {
        path: "organizations/:id/settings/api-keys",
        element: (
          <ProtectedRoute requireEmailVerified>
            <ApiKeysPage />
          </ProtectedRoute>
        ),
        loader: apiKeysLoader,
        action: createApiKeyAction,
      },
      {
        path: "organizations/:id/settings/api-keys/:keyId/edit",
        action: updateApiKeyAction,
      },
      {
        path: "organizations/:id/settings/api-keys/:keyId/revoke",
        action: revokeApiKeyAction,
      },
      {
        path: "organizations/:id/settings/api-keys/:keyId/rotate",
        action: rotateApiKeyAction,
      },
      {
        path: "organizations/:id/members/invite",
        element: (
          <ProtectedRoute requireEmailVerified>
            <InviteMemberPage />
          </ProtectedRoute>
        ),
        loader: inviteMemberLoader,
        action: inviteMemberAction,
      },
      {
        path: "organizations/:id/members/:userId/role",
        action: updateMemberRoleAction,
      },
      {
        path: "organizations/:id/members/:userId/remove",
        action: removeMemberAction,
      },
      {
        path: "organizations/:id/teams",
        element: (
          <ProtectedRoute requireEmailVerified>
            <TeamListPage />
          </ProtectedRoute>
        ),
        loader: teamListLoader,
      },
      {
        path: "organizations/:id/teams/create",
        element: (
          <ProtectedRoute requireEmailVerified>
            <CreateTeamPage />
          </ProtectedRoute>
        ),
        loader: createTeamLoader,
        action: createTeamAction,
      },
      {
        path: "organizations/:id/teams/:teamId",
        element: (
          <ProtectedRoute requireEmailVerified>
            <TeamDetailPage />
          </ProtectedRoute>
        ),
        loader: teamDetailLoader,
        action: updateTeamAction,
      },
      {
        path: "organizations/:id/teams/:teamId/delete",
        action: deleteTeamAction,
      },
      {
        path: "organizations/:id/teams/:teamId/members/add",
        element: (
          <ProtectedRoute requireEmailVerified>
            <AddTeamMemberPage />
          </ProtectedRoute>
        ),
        loader: addTeamMemberLoader,
        action: addTeamMemberAction,
      },
      {
        path: "organizations/:id/teams/:teamId/members/:userId/remove",
        action: removeTeamMemberAction,
      },
      {
        path: "organizations/:id/podcasts",
        element: (
          <ProtectedRoute requireEmailVerified>
            <PodcastListPage />
          </ProtectedRoute>
        ),
        loader: podcastListLoader,
      },
      {
        path: "organizations/:id/podcasts/create",
        element: (
          <ProtectedRoute requireEmailVerified>
            <CreatePodcastPage />
          </ProtectedRoute>
        ),
        loader: createPodcastLoader,
        action: createPodcastAction,
      },
      {
        path: "organizations/:id/podcasts/import",
        element: (
          <ProtectedRoute requireEmailVerified>
            <ImportPodcastPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "organizations/:id/podcasts/:podcastId",
        element: (
          <ProtectedRoute requireEmailVerified>
            <EpisodeListPage />
          </ProtectedRoute>
        ),
        loader: episodeListLoader,
      },
      {
        path: "organizations/:id/podcasts/:podcastId/settings",
        element: (
          <ProtectedRoute requireEmailVerified>
            <PodcastSettingsPage />
          </ProtectedRoute>
        ),
        loader: podcastDetailLoader,
        action: updatePodcastAction,
      },
      {
        path: "organizations/:id/podcasts/:podcastId/delete",
        action: deletePodcastAction,
      },
      {
        path: "organizations/:id/podcasts/:podcastId/analytics",
        element: (
          <ProtectedRoute requireEmailVerified>
            <PodcastAnalyticsPage />
          </ProtectedRoute>
        ),
        loader: podcastAnalyticsLoader,
      },
      {
        path: "organizations/:id/podcasts/:podcastId/subscribers",
        element: (
          <ProtectedRoute requireEmailVerified>
            <SubscribersPage />
          </ProtectedRoute>
        ),
        loader: subscribersLoader,
      },
      {
        path: "organizations/:id/podcasts/:podcastId/analytics/episodes/:episodeId",
        element: (
          <ProtectedRoute requireEmailVerified>
            <EpisodeAnalyticsPage />
          </ProtectedRoute>
        ),
        loader: episodeAnalyticsLoader,
      },
      {
        path: "organizations/:id/podcasts/:podcastId/episodes/create",
        element: (
          <ProtectedRoute requireEmailVerified>
            <EpisodeEditorPage mode="create" />
          </ProtectedRoute>
        ),
        loader: createEpisodeLoader,
        action: createEpisodeAction,
      },
      {
        path: "organizations/:id/podcasts/:podcastId/episodes/:episodeId/edit",
        element: (
          <ProtectedRoute requireEmailVerified>
            <EpisodeEditorPage mode="edit" />
          </ProtectedRoute>
        ),
        loader: episodeEditorLoader,
        action: updateEpisodeAction,
      },
      {
        path: "organizations/:id/podcasts/:podcastId/episodes/:episodeId/delete",
        action: deleteEpisodeAction,
      },
      {
        path: "organizations/:id/networks",
        element: (
          <ProtectedRoute requireEmailVerified>
            <NetworkListPage />
          </ProtectedRoute>
        ),
        loader: networkListLoader,
      },
      {
        path: "organizations/:id/networks/create",
        element: (
          <ProtectedRoute requireEmailVerified>
            <CreateNetworkPage />
          </ProtectedRoute>
        ),
        loader: createNetworkLoader,
        action: createNetworkAction,
      },
      {
        path: "organizations/:id/networks/:networkId/settings",
        element: (
          <ProtectedRoute requireEmailVerified>
            <NetworkSettingsPage />
          </ProtectedRoute>
        ),
        loader: networkDetailLoader,
        action: updateNetworkAction,
      },
      {
        path: "organizations/:id/networks/:networkId/members",
        element: (
          <ProtectedRoute requireEmailVerified>
            <NetworkMembershipsPage />
          </ProtectedRoute>
        ),
        loader: networkMembershipsLoader,
      },
      {
        path: "organizations/:id/networks/:networkId/memberships/invite",
        action: invitePodcastAction,
      },
      {
        path: "organizations/:id/networks/:networkId/memberships/:membershipId/accept",
        action: acceptMembershipAction,
      },
      {
        path: "organizations/:id/networks/:networkId/memberships/:membershipId/reject",
        action: rejectMembershipAction,
      },
      {
        path: "organizations/:id/networks/:networkId/memberships/:membershipId/leave",
        action: leaveMembershipAction,
      },
      {
        path: "organizations/:id/networks/:networkId/memberships/:membershipId/update-split",
        action: updateMembershipSplitAction,
      },
      {
        path: "organizations/:id/networks/:networkId/analytics",
        element: (
          <ProtectedRoute requireEmailVerified>
            <NetworkAnalyticsPage />
          </ProtectedRoute>
        ),
        loader: networkAnalyticsLoader,
      },
      {
        path: "organizations/:id/networks/:networkId/delete",
        action: deleteNetworkAction,
      },
      {
        path: "organizations/:id/networks/:networkId/tiers/create",
        action: createTierAction,
      },
      {
        path: "organizations/:id/networks/:networkId/tiers/reorder",
        action: reorderTiersAction,
      },
      {
        path: "organizations/:id/networks/:networkId/tiers/:tierId/edit",
        action: updateTierAction,
      },
      {
        path: "organizations/:id/networks/:networkId/tiers/:tierId/delete",
        action: deleteTierAction,
      },
      {
        path: "subscribe/:slug",
        element: <SubscribePage />,
        loader: subscribeLoader,
      },
      {
        path: "subscribe/:slug/success",
        element: <SubscribeSuccessPage />,
      },
      {
        path: "*",
        element: <Navigate to="/" replace />,
      },
    ],
  },
]);
